// modules/WorldInteraction/WorldMapTransition.js
// 世界场景地图转换模块 - 处理场景之间的转换

import { GAME_CONSTANTS } from '../GameConstants.js';
import GameStateStore from '../GameStateStore.js';
import AutoSaveManager from '../AutoSaveManager.js';
import { resolveTransitionTarget } from '../SceneRegistry.js';
import { transitionToWorldScene } from '../SceneTransitionHelper.js';
import DebugHelper from '../DebugHelper.js';
import { playSceneSfx } from '../LazyAssetLoader.js';

export default class WorldMapTransition {
    constructor(scene) {
        this.scene = scene;
    }
    
    /**
     * 检查地图转换点
     */
    checkMapTransitions() {
        // 如果正在过渡中，不检测传送点
        if (this.scene.isTransitioning || !this.scene.transitionPoints || !this.scene.playerSprite) {
            return;
        }
        
        const playerX = this.scene.playerSprite.x;
        const playerY = this.scene.playerSprite.y;
        
        for (const transition of this.scene.transitionPoints) {
            const { x, y, width, height, targetScene } = transition;
            
            const transitionLeft = x - width / 2;
            const transitionRight = x + width / 2;
            const transitionTop = y - height / 2;
            const transitionBottom = y + height / 2;
            
            if (playerX >= transitionLeft && playerX <= transitionRight &&
                playerY >= transitionTop && playerY <= transitionBottom) {
                
                if (this.scene.player) {
                    const tileSize = GAME_CONSTANTS.TILE_SIZE;
                    GameStateStore.syncFromScene(this.scene.player, {
                        includeVehicle: true,
                        includePosition: true,
                        position: { x: playerX, y: playerY },
                        tilePosition: {
                            tileX: Math.round((playerX - tileSize / 2) / tileSize),
                            tileY: Math.round((playerY - tileSize / 2) / tileSize)
                        },
                        inVehicle: this.scene.inVehicle
                    });
                }
                
                this.transitionToScene(
                    targetScene,
                    transition.targetX,
                    transition.targetY,
                    transition.sound
                );
                break;
            }
        }
    }
    
    /**
     * 转换到目标场景
     * @param {string} targetScene - 目标场景键名
     * @param {number} targetX - 目标X坐标（网格坐标）
     * @param {number} targetY - 目标Y坐标（网格坐标）
     * @param {string} sound - 音效键名
     * @returns {boolean} 是否已开始转换
     */
    transitionToScene(targetScene, targetX, targetY, sound = 'into') {
        if (this.scene.isTransitioning) {
            return false;
        }

        const resolved = resolveTransitionTarget(targetScene);
        if (!resolved.valid) {
            DebugHelper.error(`WorldMapTransition: 无效传送目标 "${targetScene}"`);
            this._showInvalidTargetMessage(targetScene);
            return false;
        }
        
        this.scene.isTransitioning = true;
        this.scene.movementEnabled = false;
        
        if (this.scene.bgm) {
            this.scene.bgm.stop();
        }
        
        this.scene.cameras.main.fadeOut(300, 0, 0, 0);
        
        this.scene.time.delayedCall(50, () => {
            playSceneSfx(this.scene, sound);
        });
        
        this.scene.cameras.main.once('camerafadeoutcomplete', () => {
            if (this.scene.player) {
                GameStateStore.patchPlayer({
                    currentScene: resolved.sceneKey,
                    tileX: targetX,
                    tileY: targetY
                });
                GameStateStore.setCurrentScene(resolved.sceneKey);
                AutoSaveManager.onSceneTransition();
            }

            transitionToWorldScene(this.scene, resolved.sceneKey, {
                spawnX: targetX,
                spawnY: targetY,
                transitionIn: true
            });
        });

        return true;
    }

    _showInvalidTargetMessage(targetScene) {
        const label = targetScene ? `"${targetScene}"` : '未知场景';
        const message = `无法前往 ${label}，目标场景未注册。`;

        if (this.scene.worldUI) {
            this.scene.worldUI.showMessage(message, '#ff6666');
        } else {
            DebugHelper.error(`[WorldMapTransition] ${message}`);
        }
    }
}
